import { useFormContext } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import SelectOptionComp from "@components/UI/SelectOption/Index";
import InputTextComp from "@components/UI/InputText/Index";
import SweetAlert from "@components/Feedback/SweetAlert/Index";
import { useFetchDataUnidades } from "@hooks/api/Unidade/useFetchDataUnidades";
import { useFetchDataAuditores } from "@hooks/api/Usuario/useFetchDataAuditores";
import { useFetchDataTemplatesEmails } from "@hooks/api/TemplateEmails/useFetchDataTemplatesEmails";
import InputDatePickerComp from "@components/UI/InputDatePicker/Index";
import dayjs from "dayjs";

const Create = ({ selectedRow }) => {

    const [dataLimiteSolicitacao, setDataLimiteSolicitacao] = useState("");

    const {
        register,
        watch,
        setValue,
    } = useFormContext();

    // Setar data de hoje ao carregar
    useEffect(() => {
        const today = new Date();
        const formatted = today.toISOString().split("T")[0]; // yyyy-MM-dd
        setValue("dataSolicitacao", formatted);
    }, [setValue]);


    // Atualiza data limite quando prazo ou data de envio mudam
    useEffect(() => {
        const prazo = parseInt(watch("prazoSolicitacao"), 10);
        const dataEnvio = watch("dataAgendarEnvio");
        const baseDate = dataEnvio ? new Date(dataEnvio) : new Date(watch("dataSolicitacao"));

        if (!isNaN(prazo) && prazo > 0 && prazo <= 365) {
            baseDate.setDate(baseDate.getDate() + prazo);
            const novaDataLimite = baseDate.toISOString().split("T")[0];
            setDataLimiteSolicitacao(novaDataLimite);
            setValue("dataLimiteSolicitacao", novaDataLimite);
        } else if (prazo > 365) {
            SweetAlert.error("Prazo Inválido", "O limite para o prazo é de 365 dias.");
            setValue("prazoSolicitacao", "");
        } else {
            setDataLimiteSolicitacao("");
            setValue("dataLimiteSolicitacao", "");
        }
    }, [watch("prazoSolicitacao"), watch("dataAgendarEnvio"), watch("dataSolicitacao"), setValue]);

    const {
        unitOptions,
        fetchUnits,
        unitLoading
    } = useFetchDataUnidades();

    const {
        auditoresOptions,
        auditoresLoading,
        fetchAuditores,
    } = useFetchDataAuditores();

    const {
        comunicadoOptions,
        solicitacaoOptions,
        fetchTemplates,
    } = useFetchDataTemplatesEmails();

    useEffect(() => {
        fetchUnits();
        fetchAuditores();
        fetchTemplates();
    }, []);

    const handleSelectChangeUnit = async (selectedUnit) => {
        if (!selectedRow || !selectedRow.id) {
            SweetAlert.error("Erro ao cadastrar", "Um Compliance deve ser associado antes de escolher uma Unidade/Gerência.");
            setValue("nomeUnidadeGerencia", "");
            return;
        };

        if (!selectedUnit) {
            setValue("nomeGerente", "");
            setValue("encarregado", "");

            return;
        };

        // Atualiza os valores no formulário e garante que sejam incluídos no payload
        setValue("nomeGerente", selectedUnit.nomeGerente, { shouldDirty: true });
        setValue("nomeEncarregado", selectedUnit.nomeEncarregado, { shouldDirty: true });
    };


    const handleDataAgendarEnvioChange = (date) => {
        if (!date) {
            setValue("dataAgendarEnvio", "");
            return;
        };

        const today = dayjs();

        // Verifica se a data selecionada é posterior ao "today"
        if (!date.startOf("day").isAfter(today.startOf("day"))) {
            SweetAlert.error(
                "Data inválida",
                "A data de agendamento do envio não pode estar no passado nem no dia de hoje. Por favor, escolha uma data futura."
            );
            setValue("dataAgendarEnvio", "");
            return;
        };

        // Converte para formato ISO (YYYY-MM-DD)
        const isoDate = date.format("YYYY-MM-DD");
        setValue("dataAgendarEnvio", isoDate);
    };

    return (
        <div className="form">
            <div className="row mt-3">
                <div className="col-md-12 p-0">
                    <div className="row">

                        {/* Coluna Esquerda */}
                        <div className="col-md-7 p-0">

                            <SelectOptionComp
                                label="Unidade/Gerência"
                                options={unitOptions}
                                {...register("nomeUnidadeGerencia")}
                                value={watch("nomeUnidadeGerencia")}
                                onChange={(selected) => {
                                    setValue("nomeUnidadeGerencia", selected)
                                    handleSelectChangeUnit(selected)
                                }}
                                placeholder={"Selecionar uma unidade/gerência"}
                                hoverMessage="Selecionar um unidade ou gerência"
                                isLoading={unitLoading}
                                required
                            />

                            <InputTextComp
                                label="Gerente"
                                value={watch("nomeGerente")}
                                {...register("nomeGerente")}
                                placeholder={"Gerente associado a unidade/gerência"}
                                hoverMessage="Campo automaticamente preenchido ao selecionar uma unidade/gerência. É editável."
                                required={true}
                                disabled={true}
                            />

                            <InputTextComp
                                label="Encarregado(a)"
                                value={watch("nomeEncarregado")}
                                {...register("nomeEncarregado")}
                                placeholder={"Encarregado(a) associado a unidade/gerência"}
                                hoverMessage="Campo automaticamente preenchido ao selecionar uma unidade/gerência. É editável."
                                required={true}
                                disabled={true}
                            />

                            <SelectOptionComp
                                label="Auditor(a)"
                                options={auditoresOptions}
                                value={watch("nomeAuditor")}
                                {...register("nomeAuditor")}
                                onChange={(selected) => setValue("nomeAuditor", selected)}
                                placeholder={"Selecionar um auditor(a)"}
                                hoverMessage={"Selecionar um auditor(a)"}
                                isLoading={auditoresLoading}
                                required={true}
                            />

                            <InputTextComp
                                label="Enviar E-mail CC"
                                name="emailCC"
                                {...register("emailCC")}
                                placeholder={"Preencher e-mails"}
                                hoverMessage="Preencher com os emails separados por ponto e vírgula (;), os quais receberão uma cópia de leitura. Campo Opcional."
                                maxLength={255}
                            />

                            <div className="mt-5 py-2">
                                <h2 className="ms-1">Selecione os templates de e-mails</h2>
                            </div>

                            <SelectOptionComp
                                label="Comunicado"
                                options={comunicadoOptions}
                                {...register("templateEmailComunicado")}
                                value={watch("templateEmailComunicado")}
                                onChange={(selected) => setValue("templateEmailComunicado", selected)}
                                placeholder={"Selecionar um comunicado"}
                                hoverMessage="Selecionar um comunicado"
                                required
                            />

                            <SelectOptionComp
                                label="Solicitação"
                                options={solicitacaoOptions}
                                {...register("templateEmailSolicitacao")}
                                value={watch("templateEmailSolicitacao")}
                                onChange={(selected) => setValue("templateEmailSolicitacao", selected)}
                                placeholder={"Selecionar uma solicitação"}
                                hoverMessage="Selecionar uma solicitação"
                                required
                            />

                        </div>

                        {/* Coluna Direita */}
                        <div className="col-md-5">

                            <InputTextComp
                                label="Data Solicitação"
                                name="dataSolicitacao"
                                value={watch("dataSolicitacao") || ""}
                                {...register("dataSolicitacao")}
                                hoverMessage="Campo automaticamente preenchido com a data de criação da solicitação."
                                type={"date"}
                                disabled
                                required={true}
                            />

                            <InputTextComp
                                label="Prazo"
                                name="prazo"
                                {...register("prazoSolicitacao") || ""}
                                value={watch("prazoSolicitacao")}
                                placeholder={"Preencher em dias"}
                                hoverMessage="Preencher um prazo em dias. Limite máximo de 365 dias"
                                type={"number"}
                                onInput={(e) => {
                                    let value = e.target.value.replace(/[^0-9]/g, "");

                                    if (value.length > 1) {
                                        e.target.value = value.slice(0, 3); // Limita a 3 caracteres
                                    } else {
                                        e.target.value = value.replace(/^0/, ""); // Remove o 0 apenas se for o primeiro número
                                    }
                                }}
                                required={true}
                            />

                            <InputTextComp
                                label="Data Limite"
                                name="dataLimiteSolicitacao"
                                value={watch("dataLimiteSolicitacao") || ""}
                                {...register("dataLimiteSolicitacao")}
                                hoverMessage="Campo automaticamente preenchido de acordo com a definição do prazo em dias"
                                type={"date"}
                                disabled
                                required={true}
                            />

                            <InputDatePickerComp
                                label="Agendar Envio"
                                name="dataAgendarEnvio"
                                value={watch("dataAgendarEnvio") ? dayjs(watch("dataAgendarEnvio")) : null}
                                {...register("dataAgendarEnvio")}
                                placeholder="Selecionar uma data"
                                hoverMessage="Clique no ícone ao lado para selecionar uma data se o campo estiver vazio, ou clique no X para limpar uma data selecionada."
                                format="DD/MM/YYYY"
                                showToday={false}
                                onChange={handleDataAgendarEnvioChange}
                            />

                            <div className="d-flex align-items-center mb-5">
                                <i className="bi bi-arrow-return-right fs-6 ms-1"></i>
                                <h3 className="ms-1 mt-1"> Preencha apenas para programar o envio posterior. </h3>
                            </div>

                            <div className="mt-1 mx-1 py-3">
                                <h2>Observações (Texto Simples)</h2>
                            </div>

                            <div>
                                <textarea
                                    className={`form-control text dark`}
                                    {...register("observacoes")}
                                    placeholder="Digite observações (opcional)"
                                    title="Observações(Texto Simples). Campo Opcional."
                                    style={{ height: '5rem', resize: 'none' }}
                                    maxLength={256}
                                    value={watch("observacoes") || ""}
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Create;
